import React from "react";
import FileInput from "../fileInput/FileInput";
import { useTranslator } from "context/translator";
import FormError from "components/formError/FormError";
import {
  CountrySelect,
  StateSelect,
  CitySelect,
} from "react-country-state-city";

const DocumentIdentificationForm = ({ data, onChange, form }) => {
  const { translate } = useTranslator();

  const update = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const handleToggleStatus = () => {
    onChange({ ...data, status: !data.status });
  };

  return (
    <div className="border rounded-xl p-4 space-y-4 bg-white dark:bg-gray-900 shadow">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-lg dark:text-white">
          {translate("identification-document")}
        </h3>

        {data?._id && (
          <button
            type="button"
            onClick={handleToggleStatus}
            className={`text-sm ${
              data.status ? "text-red-600" : "text-green-600"
            }`}
          >
            {data.status
              ? translate("deactivate-card")
              : translate("activate-card")}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* First Name */}
        <div>
          <label className="label">{translate("first-name")}</label>
          <input
            type="text"
            value={data.firstName || ""}
            onChange={(e) => update("firstName", e.target.value)}
            className="input"
            required
          />
          <FormError form={form} name="firstName" />
        </div>

        {/* Last Name */}
        <div>
          <label className="label">{translate("last-name")}</label>
          <input
            type="text"
            value={data.lastName || ""}
            onChange={(e) => update("lastName", e.target.value)}
            className="input"
            required
          />
          <FormError form={form} name="lastName" />
        </div>

        {/* Date of Birth */}
        <div>
          <label className="label">{translate("date-of-birth")}</label>
          <input
            type="date"
            value={data.dateOfBirth || ""}
            onChange={(e) => update("dateOfBirth", e.target.value)}
            className="input"
            required
          />
          <FormError form={form} name="dateOfBirth" />
        </div>

        {/* Expiration Date */}
        <div>
          <label className="label">{translate("expiration-date")}</label>
          <input
            type="date"
            value={data.expirationDate || ""}
            onChange={(e) => update("expirationDate", e.target.value)}
            className="input"
            required
          />
          <FormError form={form} name="expirationDate" />
        </div>
        <div>
          <label className="label">{translate("country")}</label>

          <CountrySelect
            name="country"
            defaultValue={data.country}
            containerClassName="dark:bg-gray-700 dark:fill-white"
            inputClassName="bg-gray-50 border-none text-gray-900 text-sm rounded-lg focus:ring-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500"
            onChange={(val) => onChange("country", val)}
            placeHolder={translate("country-select")}
          />
          <FormError form={form} name="country" />
        </div>

        <div>
          <label className="label">{translate("state")}</label>
          <StateSelect
            countryid={data.country?.id}
            defaultValue={data.state}
            containerClassName="dark:bg-gray-700 dark:fill-white"
            inputClassName="bg-gray-50 border-none text-gray-900 text-sm rounded-lg focus:ring-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500"
            onChange={(val) => onChange("state", val)}
            placeHolder={translate("state-select")}
          />
          <FormError form={form} name="state" />
        </div>
        <div>
          <label className="label">{translate("city")}</label>
          <CitySelect
            countryid={data.country?.id}
            stateid={data.state?.id}
            defaultValue={data.city}
            containerClassName="dark:bg-gray-700 dark:fill-white"
            inputClassName="bg-gray-50 border-none text-gray-900 text-sm rounded-lg focus:ring-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500"
            onChange={(val) => onChange("city", val)}
            placeHolder={translate("city-select")}
          />
          <FormError form={form} name="city" />
        </div>

        <div>
          <label className="label">{translate("zip-code")}</label>
          <input
            type="text"
            name="zipCode"
            value={data.zipCode}
            onChange={(e) => update("zipCode", e.target.value)}
            className="input"
          />
          <FormError form={form} name="zipCode" />
        </div>
      </div>
      <div>
        <div className="flex items-center gap-4 mb-2">
          <label className="label">{translate("street-address")}</label>
        </div>
        <textarea
          value={data.streetAddress || ""}
          onChange={(e) => update("streetAddress", e.target.value)}
          className="input"
          required
        />
        <FormError form={form} name="streetAddress" />
      </div>
      <div>
        <div className="flex items-center gap-4 mb-2">
          <label className="label">{translate("street-address-2")}</label>
        </div>
        <textarea
          value={data.streetAddress2 || ""}
          onChange={(e) => update("streetAddress2", e.target.value)}
          className="input"
          required
        />
        <FormError form={form} name="streetAddress2" />
      </div>

      {/* Files */}
      <FileInput
        label={translate("identification-files")}
        files={data.files || []}
        onAdd={(newFiles) =>
          update("files", [...(data.files || []), ...newFiles])
        }
        onRemove={(idx) =>
          update(
            "files",
            data.files.filter((_, i) => i !== idx),
          )
        }
      />
    </div>
  );
};

export default DocumentIdentificationForm;
